import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Methods } from "../../Constants/ApiConstants";
import { EndPoints } from "../../Constants/endpoints";
import logo from "../../assets/images/icons/dome-logo.svg";
import { GeneralButton, Loader } from "../../components";
import { useFetchData, usePrintPdf } from "../../hooks";

enum PdfEndpoints {
  GetPatientsReports = EndPoints.PatientsReports,
  PrintPrescription = EndPoints.ExternalPrintPrescription,
}

function PdfPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { lang, endpoint, token, Id, searchParams } = state;
  const language = (lang as "en" | "ar" | undefined) ?? "ar";
  const [isDownloadedSuccess, setIsDownloadedSuccess] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const apiEndpoint = Id
    ? endpoint == "GetPatientsReports"
      ? PdfEndpoints[endpoint as keyof typeof PdfEndpoints] + `?PatientId=${Id}`
      : `${PdfEndpoints[endpoint as keyof typeof PdfEndpoints]}/${Id}`
    : PdfEndpoints[endpoint as keyof typeof PdfEndpoints];

  const { data, isSuccess, isError } = useFetchData<any>(
    [
      PdfEndpoints[endpoint as keyof typeof PdfEndpoints],
      lang,
      endpoint,
      token,
      Id,
      searchParams,
    ],
    apiEndpoint as any,
    Methods.GET,
    {
      queryParams: searchParams,
      token: token,
      lang: language,
      options: { enabled: !!token },
    },
  );

  const { handleExportPDF } = usePrintPdf({
    enabled: false,
    file: data,
  });

  useEffect(() => {
    if (isSuccess) {
      setIsLoading(false);
      handleExportPDF()
        .then(() => {
          setIsDownloadedSuccess(true);
        })
        .catch(() => {
          setIsDownloadedSuccess(false);
        });
    }
    if (isError) {
      setIsDownloadedSuccess(false);
      setIsLoading(false);
    }
  }, [isSuccess, isError]);

  return (
    <div className="bg-primary-bg flex min-h-screen items-center justify-center p-3 sm:p-4 md:p-6">
      <div className="mx-auto flex w-full max-w-xs flex-col gap-4 rounded-xl bg-white p-5 shadow-lg sm:max-w-sm sm:rounded-2xl sm:p-6 md:max-w-md md:p-8 lg:max-w-lg">
        <img
          src={logo}
          alt="Logo"
          className="mx-auto h-12 w-auto sm:h-14 md:h-16"
        />
        {isLoading ? (
          <div className="flex flex-col items-center justify-center space-y-3 sm:space-y-4">
            <Loader size="small" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700 sm:text-base">
                {t("file_being_downloaded")}
              </p>
              <p className="mt-1 text-xs text-gray-400 sm:text-sm">
                {t("please_wait")}
              </p>
            </div>
          </div>
        ) : isDownloadedSuccess ? (
          <div className="flex flex-col items-center justify-center space-y-3 sm:space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 font-bold text-successGreen sm:h-14 sm:w-14 md:h-16 md:w-16">
              ✓
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-green-600 sm:text-base md:text-lg">
                {t("file_downloaded_successfully")}
              </p>
              <p className="mt-1 text-xs text-gray-500 sm:mt-2 sm:text-sm">
                {t("download_complete")}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-3 sm:space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 font-bold sm:h-14 sm:w-14 md:h-16 md:w-16">
              ❌
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-red-600 sm:text-base md:text-lg">
                {t("an_error_occurred_please_try_again")}
              </p>
              <p className="mt-1 text-xs text-gray-500 sm:mt-2 sm:text-sm">
                {t("error_occurred")}
              </p>
            </div>
          </div>
        )}
        <p className="text-body-text text-center text-xs sm:mt-2 sm:text-sm">
          {t("if_the_download_does_not_succeed_please_redownload_from_here")}
        </p>
        <GeneralButton
          title={t("download")}
          className="mx-auto"
          isLoading={isLoading}
          onClick={() => navigate(-1)}
        />
      </div>
    </div>
  );
}

export default PdfPage;
